// ============================================================
// SyncIndicator
// ============================================================
// Shows cloud sync status in the toolbar.
// Only visible when the user is signed in.
// Subscribes directly to the cloudSync status singleton —
// no Zustand needed, avoids unnecessary re-renders.
// ============================================================

import { useState, useEffect } from 'react';
import { getSyncStatus, subscribeSyncStatus } from '../../core/cloudSync';
import type { SyncStatus } from '../../core/types';

const LABELS: Record<SyncStatus, string> = {
  idle:    'Synced',
  syncing: 'Syncing…',
  error:   'Sync error',
  offline: 'Offline',
};

const COLORS: Record<SyncStatus, string> = {
  idle:    'var(--color-ink-muted)',
  syncing: 'var(--color-accent)',
  error:   'var(--color-error)',
  offline: '#f59e0b',
};

const DOTS: Record<SyncStatus, string> = {
  idle:    '#22c55e',  // green
  syncing: 'var(--color-accent)',
  error:   'var(--color-error)',
  offline: '#f59e0b',
};

export function SyncIndicator() {
  const [status, setStatus] = useState<SyncStatus>(getSyncStatus);

  useEffect(() => subscribeSyncStatus(setStatus), []);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        color: COLORS[status],
        letterSpacing: '0.03em',
        userSelect: 'none',
        transition: 'color 200ms ease',
      }}
      title={`Cloud sync: ${LABELS[status]}`}
    >
      {/* Status dot */}
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: DOTS[status],
          flexShrink: 0,
          transition: 'background 200ms ease',
          // Pulse animation when syncing
          animation: status === 'syncing' ? 'pulse 1s ease-in-out infinite' : 'none',
        }}
      />
      {LABELS[status]}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}
